/**
 * CPT codes page — orchestrates table rendering, save/hold actions,
 * analytics logging, and modal lifecycle for a single medical PA order.
 *
 * @module cptCodes
 */
// Sourced from the shared api-client package (canonical location) instead of
// the local organizationHelper util.
import { getOrgIdForFetchExternalWorklist } from "@risalabs_frontend_org/api-client";
import { getCurrentFacilityId } from "@risalabs_frontend_org/auth-wrapper";
import {
  SearchableCardOption,
  toast,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useBlocker, useNavigate, useParams } from "react-router-dom";
import { openModal } from "risa-oasis-ui_v2";
import CustomTable from "../../../../components/custom-table/custom-table";
import NoData from "../../../../components/noData/noData";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { useOrderFaxLettersContext } from "../../../../context/orderFaxLettersContext";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { JCodeDetails } from "../../../../data-model/medicalPaOrdersModel";
import { AuthLetterReviewStatus } from "../../../../enums/authLetterReviewStatus";
import { AuthOnFileRpaErrorType } from "../../../../enums/authOnFileRpaErrorType";
import { DropdownLocation } from "../../../../enums/dropdownLocation";
import { ModalId } from "../../../../enums/modalId";
import {
  isExceptionFlowForUrologyOrg,
  isUrologyOrRadiologyOrg,
} from "../../../../enums/organizationTypes";
import { useAuthOnFileError } from "../../../../hooks/useAuthOnFileError";
import { useCptCodesMappingForAnOrg } from "../../../../hooks/useCptCodesMappingForAnOrg";
import { useFacilityCategory } from "../../../../hooks/useOncoemrTasksEnabled";
import { setOpenedModalId } from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { getFilingInfoValueForDisplay } from "../../../../utils/getFilingInfoValue";
import { getInvalidRequiredFieldNames } from "../../../authorisationDetails/utils/invalidRequiredFields";
import StatusError from "../../../singleOrderLayout/components/errorComponents/statusError";
import { getAllJCodesFromAuthOnFile } from "../../../singleOrderLayout/utils/existingDocsToLs";
import CptCodesModals from "./components/CptCodesModals";
import CptCodesToolbar from "./components/CptCodesToolbar";
import DenialQueryBucket from "./components/DenialQueryBucket";
import NoCptCodesFound from "./components/NoCptCodesFound";
import { getCptCodesTableColumnsKeys } from "./cptCodesTable";
import { useCptAnalytics } from "./hooks/useCptAnalytics";
import { useCptSaveActions } from "./hooks/useCptSaveActions";
import { useCptTableState } from "./hooks/useCptTableState";
import { useDenialQueryBucket } from "./hooks/useDenialQueryBucket";
import { useOpenModalOnIdChange } from "./hooks/useOpenModalOnIdChange";
import { getAuthLetterMismatchesForRow } from "./utils/authLetterFieldComparison";
import { handleAuthLetterUpload } from "./utils/authLetterModalHandlers";
import {
  applyFaxLetterIcon,
  faxLetterDetailsPath,
} from "./utils/faxLetterDocument";
import { handleValueChangeOfTextField } from "./utils/handleValueChangeOfTextField";
import { logCptNewDescriptionSelected } from "./utils/logCptNewDescriptionSelected";
import { navigateToAuthorisationDetails } from "./utils/navigateToAuthorisationDetails";

/**
 * CPT / J-code editor route that renders the denial-query summary, editable
 * table, save flows, navigation blockers, and modals.
 *
 * @returns CPT codes surface with denial-query summary and fallback states.
 */
const CptCodes = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    generalOrderDetails,
    authOnFileDetails,
    authorizationData,
    authorizationDataLoading: isAuthorizationDataLoading,
  } = useDataAccumulatorForSingleOrder();

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const extractionByJCode = useSelector(
    (state: RootState) => state.authLetterExtraction,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const unwrappedGeneralOrderDetails = generalOrderDetails?.order ?? null;
  const unwrappedAuthOnFileDetails = authOnFileDetails?.auth_on_file ?? null;
  const { byJCodeId: faxLettersByJCodeId } = useOrderFaxLettersContext();

  // Columns are built at render time (rather than as a module-level const)
  // so the facility-dependent column label (e.g. "CPT Codes" for Astera
  // Radiology vs. "J Codes" elsewhere) reflects the facility id resolved
  // after auth/token initialization. Memoized to avoid rebuilding the
  // header array on every render.
  const facilityId = getCurrentFacilityId();
  const facilityCategory = useFacilityCategory();

  // SEARCHABLE_CARD options for the editable description cell. We mirror the
  // hook output into local state so future flows (e.g. an "add option"
  // affordance on the searchable card) can append entries optimistically
  // without waiting for a Firestore refetch. The sync `useEffect` below
  // keeps the local state in lockstep with the hook's memoised options.
  const [options, setOptions] = useState<SearchableCardOption[]>([]);
  const { cptCodesMappingOptions } = useCptCodesMappingForAnOrg(
    facilityId ?? "",
  );

  const cptCodesTableColumns = useMemo(
    () => getCptCodesTableColumnsKeys(facilityId, facilityCategory),
    [facilityId, facilityCategory],
  );

  const tableState = useCptTableState({
    orderId,
    authStatusOptions: authStatusOptions ?? [],
    generalOrderDetails: unwrappedGeneralOrderDetails,
    authOnFileDetails: unwrappedAuthOnFileDetails,
    options: options,
  });

  // Index the auth-on-file J-codes by id so each table row can be matched to
  // the agent-extracted `auth_letter` values shown on the Authorization
  // Details screen.
  const jCodesById = useMemo(() => {
    const map = new Map<string, JCodeDetails>();
    (getAllJCodesFromAuthOnFile(unwrappedAuthOnFileDetails ?? undefined) ?? [])
      .filter((jCode) => Boolean(jCode?.id))
      .forEach((jCode) => map.set(jCode.id as string, jCode));
    return map;
  }, [unwrappedAuthOnFileDetails]);

  // Derived from live table state so the mismatch badge + Save gating react
  // to edits: when a user aligns a drifted field with the auth letter, the
  // badge clears and Save Changes re-enables (avoiding a save deadlock).
  // Gate on `isVisible` (live Auth Required === Auth on File) so mismatches
  // from persisted auth_on_file do not keep Save blocked after the user
  // leaves Auth on File and the upload/mismatch UI is hidden.
  // `filingInfo.value` stores the raw filing mode, so the badge label is
  // derived here (from mode + detail + live auth status) rather than being
  // duplicated into row state.
  const displayTableData = useMemo(
    () =>
      tableState.tableData.map((row) => {
        const mismatchFields = row.docWithUploadIcon?.isVisible
          ? getAuthLetterMismatchesForRow(row, jCodesById.get(row.jCodeId))
          : [];
        // Dark-green (uploaded, unreviewed) rows get the invalid-values badge
        // from the persisted auth-letter values. Mirror the render gate in
        // IsDocumentUploaded so the computed data and the displayed badge agree.
        const docIcon = row.docWithUploadIcon;
        const isDarkGreen =
          !!docIcon &&
          docIcon.isVisible &&
          docIcon.isUploaded &&
          docIcon.authLetterReviewStatus !==
            AuthLetterReviewStatus.NewLetterUploaded &&
          docIcon.authLetterReviewStatus !==
            AuthLetterReviewStatus.ReviewedAndSaved;
        const invalidFieldNames = isDarkGreen
          ? getInvalidRequiredFieldNames(jCodesById.get(row.jCodeId))
          : [];
        const hasMismatches = mismatchFields.length > 0;
        const hasInvalid = invalidFieldNames.length > 0;
        return applyFaxLetterIcon(
          {
            ...row,
            filingInfo: {
              ...row.filingInfo,
              value: getFilingInfoValueForDisplay(
                row.paRequired?.currentStatusId,
                row.filingInfo?.value,
                row.filingInfo?.detail,
              ),
            },
            ...(hasMismatches || hasInvalid
              ? {
                  docWithUploadIcon: {
                    ...row.docWithUploadIcon,
                    ...(hasMismatches ? { mismatchFields } : {}),
                    ...(hasInvalid ? { invalidFieldNames } : {}),
                  },
                }
              : {}),
          },
          faxLettersByJCodeId.get(row.jCodeId),
        );
      }),
    [tableState.tableData, jCodesById, faxLettersByJCodeId],
  );

  const hasAuthLetterFieldMismatch = useMemo(
    () =>
      displayTableData.some(
        (row) => (row.docWithUploadIcon?.mismatchFields?.length ?? 0) > 0,
      ),
    [displayTableData],
  );

  const blocker = useBlocker(tableState.hasChanges);

  const saveActions = useCptSaveActions({
    generalOrderDetails: unwrappedGeneralOrderDetails,
    authOnFileDetails: unwrappedAuthOnFileDetails,
    orderId,
    user,
    payerName,
    authStatusOptions: authStatusOptions ?? [],
    tableData: tableState.tableData,
    originalTableData: tableState.originalTableData,
    hasChanges: tableState.hasChanges,
    setTableData: tableState.setTableData,
    setOriginalTableData: tableState.setOriginalTableData,
    setHasChanges: tableState.setHasChanges,
    saveTableDataToContext: tableState.saveTableDataToContext,
    removeBoComments: tableState.removeBoComments,
    removeCptCodes: tableState.removeCptCodes,
    removeAuthStatusChangeComments: tableState.removeAuthStatusChangeComments,
    dispatch,
    blockerProceed: blocker.proceed,
  });

  const analytics = useCptAnalytics({
    generalOrderDetails: unwrappedGeneralOrderDetails,
    authOnFileDetails: unwrappedAuthOnFileDetails,
    user,
    payerName,
    tableData: tableState.tableData,
  });

  useOpenModalOnIdChange(openedModalId);

  useEffect(() => {
    if (blocker.state === "blocked") {
      openModal(ModalId.PAGE_LEAVE_CONFIRMATION_MODAL);
    }
  }, [blocker.state]);

  useEffect(() => {
    if (openedModalId === ModalId.STATUS_CHANGED_TO_HOLD_MODAL) {
      openModal(ModalId.STATUS_CHANGED_TO_HOLD_MODAL);
    }
    if (openedModalId === ModalId.DOC_VIEWER_MODAL_WITH_TITLE) {
      openModal(ModalId.DOC_VIEWER_MODAL_WITH_TITLE);
    }
    if (openedModalId === ModalId.CPT_NEW_DESCRIPTION_MODAL) {
      openModal(ModalId.CPT_NEW_DESCRIPTION_MODAL);
    }
    if (openedModalId === ModalId.FILING_INFO_MODAL) {
      openModal(ModalId.FILING_INFO_MODAL);
    }
  }, [openedModalId]);

  // Mirror the latest Firestore-derived options into local state. When the
  // mapping resolves (or refetches), this triggers
  // `useSyncTableDataWithOrder` (which now depends on `options`) to
  // regenerate row data so the searchable-card dropdown actually has
  // entries to show.
  useEffect(() => {
    setOptions(cptCodesMappingOptions);
  }, [cptCodesMappingOptions]);

  const {
    shouldShowError: shouldShowRegimenNotFound,
    errorMessage,
    dropdownLocation,
  } = useAuthOnFileError(AuthOnFileRpaErrorType.REGIMEN_NOT_FOUND);

  const shouldShowDropdownInCpt =
    dropdownLocation === DropdownLocation.CPT ||
    dropdownLocation === DropdownLocation.BOTH;
  const { shouldShowDenialQueryBucket, ...denialQueryBucketDetails } =
    useDenialQueryBucket();

  return (
    <div className="cpt-codes-table--container flex h-auto max-h-full flex-col">
      {shouldShowRegimenNotFound ? (
        <StatusError
          title={errorMessage}
          showDropdown={shouldShowDropdownInCpt}
        />
      ) : (
        <div className="cpt-codes-table--container flex h-full max-h-full flex-col">
          {tableState.tableData.length > 0 ? (
            <>
              {shouldShowDenialQueryBucket && (
                <DenialQueryBucket {...denialQueryBucketDetails} />
              )}
              <div className="cpt-codes-table--container--table">
                <CustomTable
                  tableHeaders={cptCodesTableColumns}
                  tableData={displayTableData}
                  itemsPerPage={tableState.tableData.length}
                  pagesPerView={5}
                  count={tableState.tableData.length}
                  onRowClick={() => {}}
                  onClickableBadgeClick={(id) =>
                    tableState.openFilingInfoModal(id ?? "")
                  }
                  tableName="cptCodes"
                  hideSearchBar={true}
                  hidePagination={true}
                  onTableRowUpdate={tableState.handleTableRowUpdate}
                  onCreateOption={(value, id) => {
                    dispatch(
                      setOpenedModalId({
                        id: ModalId.CPT_NEW_DESCRIPTION_MODAL,
                        metaData: {
                          description: value,
                          id,
                          cptCode: tableState.tableData.find(
                            (row) => row.jCodeId === id,
                          )?.jCodes?.data,
                        },
                      }),
                    );
                  }}
                  onOptionClick={(option, id) =>
                    logCptNewDescriptionSelected({
                      option,
                      rowId: id,
                      tableData: tableState.tableData,
                      availableOptions: options,
                      generalOrderDetails: unwrappedGeneralOrderDetails,
                    })
                  }
                  onValueChangeOfTextField={(value, id, type) =>
                    handleValueChangeOfTextField(
                      value,
                      id,
                      type ?? "",
                      tableState.tableData,
                      tableState.originalTableData,
                      tableState.setTableData,
                      tableState.setHasChanges,
                      tableState.saveTableDataToContext,
                      unwrappedGeneralOrderDetails,
                      unwrappedAuthOnFileDetails,
                      facilityCategory,
                    )
                  }
                  onUploadClick={(_, __, jCodeId, authStatus) => {
                    if (jCodesById.get(jCodeId)?.auth_status !== authStatus) {
                      toast.error(
                        ToastMessages.AUTH_LETTER_UPLOAD_NEEDS_SAVED_STATUS,
                      );
                      return;
                    }
                    handleAuthLetterUpload(
                      dispatch,
                      authStatus,
                      jCodeId,
                      undefined,
                      authorizationData ?? undefined,
                      isAuthorizationDataLoading ?? false,
                    );
                  }}
                  onSuccessClick={(
                    _,
                    jCode,
                    jCodeId,
                    authStatus,
                    authLetterReviewStatus,
                  ) => {
                    const faxLetter = faxLettersByJCodeId.get(jCodeId);
                    if (faxLetter) {
                      navigate(
                        faxLetterDetailsPath(
                          orderId ?? "",
                          jCodeId,
                          faxLetter.document_id,
                        ),
                      );
                      return;
                    }
                    // TODO: Add navigate to authorisation details from postgres and removing singleorer details
                    navigateToAuthorisationDetails({
                      jCodeId,
                      jCode,
                      authStatus,
                      authOnFileDetails: unwrappedAuthOnFileDetails,
                      orderId: orderId ?? "",
                      navigate,
                      letterDocPathOverride:
                        extractionByJCode.documentPathByJCode[
                          `${orderId ?? ""}:${jCodeId}`
                        ] ??
                        extractionByJCode.latestDocumentByJCode[
                          `${orderId ?? ""}:${jCodeId}`
                        ]?.documentPath,
                      letterTitleOverride:
                        extractionByJCode.latestDocumentByJCode[
                          `${orderId ?? ""}:${jCodeId}`
                        ]?.documentTitle,
                      agentRerunLoading:
                        extractionByJCode.loadingByJCode[
                          `${orderId ?? ""}:${jCodeId}`
                        ],
                    });
                  }}
                  onIconCellClick={(rowData) => {
                    dispatch(
                      setOpenedModalId({
                        id: ModalId.DELETE_CONFIRMATION_MODAL,
                        metaData: {
                          itemId: rowData?.id ?? "",
                          cptCode: rowData?.j_code ?? "",
                          cptDescription: rowData?.medicine_name ?? "",
                          isDeleting: false,
                        },
                      }),
                    );
                  }}
                />
              </div>
              <CptCodesToolbar
                hasChanges={tableState.hasChanges}
                isSaving={saveActions.isSaving}
                hasCptTableAnyError={tableState.hasCptTableAnyError}
                hasAuthLetterFieldMismatch={hasAuthLetterFieldMismatch}
                openedModalId={openedModalId}
                tableData={tableState.tableData}
                parentDateOfService={
                  unwrappedGeneralOrderDetails?.payload?.date_of_service
                }
                onReportInaccuracy={analytics.logReportInaccuracyClicked}
                onSaveLog={analytics.handleSaveLog}
                onSaveChanges={saveActions.handleSaveChanges}
                dispatch={dispatch}
                showAddProcedure={isUrologyOrRadiologyOrg(facilityCategory)}
                confirmationSource={
                  isExceptionFlowForUrologyOrg(
                    getOrgIdForFetchExternalWorklist(),
                  )
                    ? "cpt_codes"
                    : "j_codes"
                }
              />
            </>
          ) : isUrologyOrRadiologyOrg(facilityCategory) ? (
            // Urology/radiology facilities can add CPT codes manually, so the
            // empty state offers an "Add CPT Code" action instead of a dead end.
            <div className="flex h-full items-center justify-center">
              <NoCptCodesFound />
            </div>
          ) : (
            // Other facilities have no manual add flow; show the generic
            // no-data placeholder.
            <div className="flex h-full items-center justify-center rounded-lg bg-gray-50">
              <NoData />
            </div>
          )}
        </div>
      )}
      <>
        <CptCodesModals
          openedModalId={openedModalId}
          blockerState={blocker.state}
          generalOrderDetails={unwrappedGeneralOrderDetails}
          authOnFileDetails={unwrappedAuthOnFileDetails}
          originalTableData={tableState.originalTableData}
          tableData={tableState.tableData}
          parentDateOfService={
            unwrappedGeneralOrderDetails?.payload?.date_of_service
          }
          hasChanges={tableState.hasChanges}
          hasCptTableAnyError={tableState.hasCptTableAnyError}
          selectedRowData={tableState.selectedRowData}
          selectedRowDataForReference={tableState.selectedRowDataForReference}
          isSaving={saveActions.isSaving}
          isHoldSaving={saveActions.isHoldSaving}
          setIsSaving={saveActions.setIsSaving}
          setTableData={tableState.setTableData}
          setHasChanges={tableState.setHasChanges}
          saveTableDataToContext={tableState.saveTableDataToContext}
          handleFilingInfoSave={tableState.handleFilingInfoSave}
          handleSaveChanges={saveActions.handleSaveChanges}
          handleSaveHold={saveActions.handleSaveHold}
          handleProceedWithoutSaving={saveActions.handleProceedWithoutSaving}
          onAuthLetterUploadSuccess={(jCodeId, docPath, documentTitle) => {
            navigate(`/worklists/authorisation-details/${orderId ?? ""}`, {
              state: {
                jCodeId,
                letterDocPath: docPath,
                letterTitle: documentTitle,
                agentRerunLoading: true,
                jCode: jCodesById.get(jCodeId),
              },
            });
          }}
          dispatch={dispatch}
        />
      </>
    </div>
  );
};

export default CptCodes;
